"use client";
import { useEffect, useMemo, useState } from "react";
import { filter as lodashFilter, orderBy } from "lodash";
import LocationService, { Location } from "@/app/_services/LocationService";
import { toast } from "@/app/_utils/toast";

export function useLocationsModel() {
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [openForm, setOpenForm] = useState(false);

  async function loadLocations() {
    setIsLoading(true);
    const res = await LocationService.getLocationsAsync();
    setLocations(orderBy(res, [(l) => l.last_show_date ?? ""], ["desc"]));
    setIsLoading(false);
  }

  useEffect(() => {
    loadLocations();
  }, []);

  const filteredLocations = useMemo(() => {
    if (!search.trim()) return locations;
    const q = search.trim().toLowerCase();
    return lodashFilter(
      locations,
      (l) =>
        l.name?.toLowerCase().includes(q) ||
        l.city?.toLowerCase().includes(q) ||
        l.state?.toLowerCase().includes(q)
    );
  }, [locations, search]);

  function openCreateForm() {
    setSelectedLocation(null);
    setOpenForm(true);
  }

  function openEditForm(location: Location) {
    setSelectedLocation(location);
    setOpenForm(true);
  }

  function closeForm() {
    setOpenForm(false);
  }

  async function handleDelete(location: Location) {
    try {
      await LocationService.deleteLocationAsync(location.id);
      toast.success("Location deleted");
      setSelectedLocation((current) => (current?.id === location.id ? null : current));
      await loadLocations();
    } catch (e) {
      toast.error("Failed to delete location");
    }
  }

  return {
    locations,
    filteredLocations,
    isLoading,
    search,
    setSearch,
    selectedLocation,
    setSelectedLocation,
    openForm,
    openCreateForm,
    openEditForm,
    closeForm,
    handleDelete,
    refetch: loadLocations,
  };
}
